/**
 * Brand Name Resolver Hook
 *
 * Single source of truth for the user-facing brand/app name.
 *
 * Resolution order:
 *   1. Backend config `branding.name[lang]` (canonical — from `config/app.yaml`
 *      via `GET /config`, deployment-specific).
 *   2. Backend config `branding.name.de` (guard for an unexpected/missing
 *      active language — mirrors the legacy useDocumentTitle behaviour).
 *   3. i18n `app.name` (fallback while config is loading or absent, e.g. the
 *      hardcoded fallback config that omits branding).
 *
 * Every brand display in the app must go through this hook so the brand name
 * has exactly one resolver and one fallback.
 */

import { useTranslation } from 'react-i18next';
import { useConfigStore } from '@/stores/config';

/**
 * Returns the resolved brand name for the current i18n language.
 *
 * Works during config load: when `config` is null (e.g. AppLoading renders
 * before `/config` resolves) it falls back to the i18n `app.name` string.
 */
export function useBrandName(): string {
  const { t, i18n } = useTranslation();
  const config = useConfigStore((state) => state.config);

  const branding = config?.branding?.name;
  if (branding) {
    // i18n.language can be a region variant (e.g. "de-CH" for Swiss German)
    // that isn't a SupportedLanguage key — look it up as possibly-missing and
    // fall back to German. The `?? branding.de` is then type-meaningful, not
    // dead code masked by an unsound cast.
    const byLang = (branding as Record<string, string | undefined>)[i18n.language];
    return byLang ?? branding.de;
  }

  return t('app.name');
}
